import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoChevronBack } from 'react-icons/io5';
import { CiHeart } from 'react-icons/ci';
import { PiChatCircleDots } from 'react-icons/pi';
import { FaHeart } from 'react-icons/fa';
import { getBookcommunityBooks, getBookcommunityLikedBookIds, addBookcommunityBookLike, deleteBookcommunityBookLike } from '../api/BookcommunityApi';
import { getAccessToken } from '../react-queries/queries';
import { processApiResponse, ERROR_CODES } from '../common/errorMessage';
import LoadingOverlay from '../components/LoadingOverlay';
import MessageModal from '../components/MessageModal';
import MoimDetailCommunityBookDetailPage from './MoimDetailCommunityBookDetailPage';
import { FaTimes } from 'react-icons/fa';
import BookCoverImage from '../components/BookCoverImage';


const Wrapper = styled.div`
  max-width: 480px;
  margin: 0 auto;
  background: #fff;
  min-height: 100vh;
`;

const Content = styled.div`
  padding: 0 0 50px 0;
`;


// 커뮤니티 탭용 스타일
const CommunityContent = styled.div`
  padding: 20px;
`;


const CommunityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 16px 0;
  background: #f8f9fa;
  padding: 8px 20px;
`;

const BookCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #222;
`;

const SortDropdown = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #222;
  cursor: pointer;
`;

const BookItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
`;

const BookItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const BookItemTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 4px;
`;

const BookItemDesc = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`;

const BookItemStats = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #999;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;


const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  height: 100vh;
  background: #fff;
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fff;
`;

const ModalTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #222;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background: #f5f5f7;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 20px 20px;
`;





const PAGE_SIZE = 50;


const MoimDetailCommunityBookDetailModal = ({ bookCommunityBookId, onClose }) => {
  const [isModified, setIsModified] = useState(false);
  return (
    <Modal>
      <ModalHeader>
        <ModalTitle>도서 상세</ModalTitle>
        <CloseButton onClick={()=>{onClose(isModified);}}>
          <FaTimes />
        </CloseButton>
      </ModalHeader>
      <ModalContent>
        <MoimDetailCommunityBookDetailPage bookCommunityBookId={bookCommunityBookId} onModified={setIsModified} />
      </ModalContent>
    </Modal>
  )
};




function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function MoimDetailCommunityBookListPage({bookCommunityId , refreshFlag}) {
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [isModified, setIsModified] = useState(false);
 
  // API 데이터 관련 상태
  const [bookList, setBookList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [sortByNew, setSortByNew] = useState(true);
  
  // 좋아요 관련 상태
  const [bookLikes, setBookLikes] = useState({});
  const [likedBookIds, setLikedBookIds] = useState(new Set());
  
  // 메시지 모달 상태
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalText, setMessageModalText] = useState('');
  const [showCommunityBookDetailModal, setShowCommunityBookDetailModal] = useState(false);
  const [selectedBookCommunityBookId, setSelectedBookCommunityBookId] = useState(null);

  // 좋아요한 책 ID 목록 가져오기
  const fetchLikedBookIds = useCallback(async () => {
    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        return;
      }
      
      const response = await getBookcommunityLikedBookIds(accessToken, bookCommunityId);
      const processedResponse = processApiResponse(response);
      
      if (processedResponse.success) {
        const likedIds = processedResponse.data || [];
        setLikedBookIds(new Set(likedIds));
      }
    } catch (err) {
      console.error('Error fetching liked book IDs:', err);
    }
  }, [bookCommunityId, refreshFlag]);

  // 도서 목록 가져오기
  const fetchBooks = useCallback(async (page = 0, reset = false) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }
      
      const response = await getBookcommunityBooks(
        accessToken,
        bookCommunityId,
        page,
        PAGE_SIZE,
        sortByNew
      );

      const processedResponse = processApiResponse(response);
      
      if (processedResponse.success) {
        const newBooks = processedResponse.data || [];   
        if (reset) {
          setBookList(newBooks);
          setCurrentPage(0);
        } else {
          setBookList(prev => [...prev, ...newBooks]);
        }
        
        setHasMore(newBooks.length === PAGE_SIZE);
        setTotalCount(response.page?.total || 0); //newBooks.length);
        
        // 좋아요 상태 초기화
        const likesState = {};
        newBooks.forEach(book => {
          likesState[book.id] = book.likeCnt || 0;
        });
        setBookLikes(prev => ({ ...prev, ...likesState }));
      } else {
        if (response?.errorCode === ERROR_CODES.UNAUTHORIZATIONERROR) {
          setError('모임 멤버만 볼 수 있는 공간입니다.\n 참여 신청 후 멤버들과 함께 이야기를 나눠보세요!');
        } else {
          setError(processedResponse.errorMessage);
        }
      }
        
    } catch (err) {
      setError('도서 목록을 가져오는 중 오류가 발생했습니다.');
      console.error('Error fetching books:', err);
    } finally {
      setLoading(false);
    }
  }, [bookCommunityId, sortByNew, refreshFlag, isModified]);

useEffect(()=>{

  console.log('bookCommunityId', bookCommunityId);
}, [bookCommunityId, refreshFlag]);

  // 초기 데이터 로드
  useEffect(() => {
    console.log('bookCommunityId', bookCommunityId);
    setIsModified(false);
    if (bookCommunityId) {
      fetchBooks(0, true);
      fetchLikedBookIds();
    }
  }, [bookCommunityId, sortByNew, refreshFlag]);



  useEffect(() => {
    console.log('isModified', isModified);
    
    if (bookCommunityId && isModified) {
      fetchBooks(0, true);
      fetchLikedBookIds();
    }
    setIsModified(false);
  }, [isModified]);

  // 무한스크롤 처리
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchBooks(nextPage, false);
    }
  }, [hasMore, loading, currentPage]);


  useEffect(() => {
    //console.log('handleScroll register', document.getElementById('noobook-page-modal'));
    const modalEle = document.getElementById('noobook-page-modal')
    modalEle?.addEventListener('scroll', handleScroll);
    return () => {
      modalEle?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

 

  // 정렬 변경 핸들러
  const handleSortChange = () => {
    setSortByNew(!sortByNew);
  };

  // 도서 하트 클릭 핸들러
  const handleBookLikeClick = async (bookId, e) => {
    e.stopPropagation(); // 부모 요소의 클릭 이벤트 방지
    
    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        setMessageModalText('로그인이 필요합니다.');
        setShowMessageModal(true);
        return;
      }
      
      if (likedBookIds.has(bookId)) {
        // 이미 좋아요를 눌렀다면 취소
        const response = await deleteBookcommunityBookLike(accessToken, bookId);
        const processedResponse = processApiResponse(response);
        
        if (processedResponse.success) {
          setLikedBookIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(bookId);
            return newSet;
          });
          setBookLikes(prev => ({
            ...prev,
            [bookId]: Math.max(0, (prev[bookId] || 0) - 1)
          }));
        } else {
          setMessageModalText(processedResponse.errorMessage || '좋아요 취소에 실패했습니다.');
          setShowMessageModal(true);
        }
      } else {
        // 좋아요 누르기
        const response = await addBookcommunityBookLike(accessToken, bookId);
        const processedResponse = processApiResponse(response);
        
        if (processedResponse.success) {
          setLikedBookIds(prev => new Set([...prev, bookId]));
          setBookLikes(prev => ({
            ...prev,
            [bookId]: (prev[bookId] || 0) + 1
          }));
        } else {
          setMessageModalText(processedResponse.errorMessage || '좋아요에 실패했습니다.');
          setShowMessageModal(true);
        }
      }
    } catch (err) {
      console.error('Error handling book like:', err);
      setMessageModalText('좋아요 처리 중 오류가 발생했습니다.');
      setShowMessageModal(true);
    }
  };

  const handleCommunityBookDetailModal = (bookCommunityBookId) => {
    setShowCommunityBookDetailModal(true);
    setSelectedBookCommunityBookId(bookCommunityBookId);
  };

  const closeCommunityBookDetailModal = (modified) => {
    setShowCommunityBookDetailModal(false);
    setIsModified(modified);
  };

  return (
    <Wrapper>
      {/*<Content onScroll={handleScroll} style={{ height: '100vh', overflowY: 'auto' }}>*/}
          <Content>
          <CommunityContent>
                <CommunityHeader>
                  <BookCount>{totalCount}권의 책</BookCount>
                  <SortDropdown onClick={handleSortChange}>
                    {sortByNew ? '최신순' : '오래된순'}
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="#222" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </SortDropdown>
                </CommunityHeader>
                
                {error && (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666', whiteSpace: 'pre-line' }}>
                    {error}
                  </div>
                )}
                
                {bookList.map((book) => (
                  <BookItem 
                    key={book.id} 
                    onClick={() => handleCommunityBookDetailModal(book.id)} //() => navigate('/booklist-detail?title=' + encodeURIComponent(book.book?.title || ''))} 
                    style={{cursor: 'pointer'}}
                  >
                    <BookCoverImage
                      src={book.book?.cover}
                      alt={book.book?.title}
                      width={60}
                      height={80}
                      boxShadow="none"
                      fontSize={9}
                    />
                    <BookItemInfo>
                      <div>
                        <BookItemTitle>{book.book?.title || '제목 없음'}</BookItemTitle>
                        <BookItemDesc>{book.bookIntroduction || book.book?.author || '설명 없음'}</BookItemDesc>
                      </div>
                      <BookItemStats>
                        <StatItem onClick={(e) => {
                          e.stopPropagation();
                          handleBookLikeClick(book.id, e);
                        }} style={{cursor: 'pointer'}}>
                          {likedBookIds.has(book.id) ? (
                            <FaHeart size={12} color="#e74c3c" />
                          ) : (
                            <CiHeart size={12} color="#999" />
                          )}
                          {bookLikes[book.id] || 0}
                        </StatItem>
                        <StatItem>
                          <PiChatCircleDots size={12} color="#999" />
                          {book.commentCnt || 0}
                        </StatItem>
                      </BookItemStats>
                    </BookItemInfo>
                  </BookItem>
                ))}
                
                {loading && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    로딩 중...
                  </div>
                )}
                
                {!hasMore && bookList.length > 0 && (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    모든 도서를 불러왔습니다.
                  </div>
                )}
                
                {bookList.length === 0 && !loading && !error && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    등록된 도서가 없습니다.
                  </div>
                )}
          </CommunityContent>
      </Content>
      
      {showMessageModal && (
        <MessageModal
          text={messageModalText}
          onClose={() => setShowMessageModal(false)}
        />
      )}
       {/*  도서  상세 모달 */}
       {showCommunityBookDetailModal && (
        <MoimDetailCommunityBookDetailModal 
          bookCommunityBookId={selectedBookCommunityBookId} 
          onClose={closeCommunityBookDetailModal}
        />
      )}
      
      {loading && <LoadingOverlay />}
    </Wrapper>
  );
} 